import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { themes, countries, citiesByCountry, type Country } from "@/data/photographers";
import { visagistThemes } from "@/data/visagists";
import { z } from "zod";
import { toWebP, safeContentType, assertUploadable, UPLOAD_MAX_INPUT_BYTES, isStaticImage, STATIC_IMAGE_ACCEPT } from "@/lib/image-convert";
import { getSettingInt, SETTING_PHOTOGRAPHER_MAX_GALLERY, SETTING_VISAGIST_MAX_GALLERY } from "@/lib/app-settings";

export const Route = createFileRoute("/apply")({
  validateSearch: (s: Record<string, unknown>) => ({
    as: s.as === "visagist" ? "visagist" : "photographer",
  }),
  head: () => ({
    meta: [
      { title: "Als Fotograf:in & Visagist:in gelistet werden, OM Selected" },
      { name: "description", content: "Werde als Fotograf:in oder Visagist:in bei OM Selected gelistet. Eigenes Profil, mehr Sichtbarkeit und Kundenanfragen aus DACH, kuratiert statt Marktplatz." },
      { property: "og:title", content: "Als Fotograf:in & Visagist:in gelistet werden, OM Selected" },
      { property: "og:description", content: "Gelistet werden auf einer kuratierten Plattform für Fotograf:innen und Visagist:innen, eigenes Profil, Sichtbarkeit und direkte Kundenanfragen." },
      { property: "og:url", content: "https://omselected.art/apply" },
    ],
    links: [{ rel: "canonical", href: "https://omselected.art/apply" }],
  }),
  component: ApplyPage,
});

function ApplyPage() {
  const { as } = Route.useSearch();
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const [country, setCountry] = useState<Country | "">("");
  const [cityChoice, setCityChoice] = useState<string>("");
  const [kind, setKind] = useState<"photographer" | "visagist">(as);
  const [consent, setConsent] = useState(false);
  const [igConsent, setIgConsent] = useState(false);
  const [samples, setSamples] = useState<{ file: File; credit: string; theme: string }[]>([]);
  const [portrait, setPortrait] = useState<File | null>(null);
  const themeOptions = kind === "visagist" ? visagistThemes : themes;
  const defaultTheme = themeOptions[0]?.id ?? "open";

  const [maxPhotographer, setMaxPhotographer] = useState(10);
  const [maxVisagist, setMaxVisagist] = useState(10);
  const MAX_SAMPLES = kind === "visagist" ? maxVisagist : maxPhotographer;

  const MAX_FILE_BYTES = UPLOAD_MAX_INPUT_BYTES;

  useEffect(() => {
    getSettingInt(SETTING_PHOTOGRAPHER_MAX_GALLERY, 10).then(setMaxPhotographer);
    getSettingInt(SETTING_VISAGIST_MAX_GALLERY, 10).then(setMaxVisagist);
  }, []);

  function addFiles(list: FileList | null) {
    if (!list) return;
    const incoming = Array.from(list);
    const valid: { file: File; credit: string; theme: string }[] = [];
    for (const f of incoming) {
      if (!isStaticImage(f)) { setError(t("applyPage.errInvalidFormat", { name: f.name })); continue; }
      if (f.size > MAX_FILE_BYTES) { setError(t("applyPage.errTooBig", { name: f.name })); continue; }
      valid.push({ file: f, credit: "", theme: defaultTheme });
    }
    setSamples((prev) => [...prev, ...valid].slice(0, MAX_SAMPLES));
  }

  function setPortraitFile(list: FileList | null) {
    const f = list?.[0];
    if (!f) return;
    if (!isStaticImage(f)) { setError(t("applyPage.errInvalidFormat", { name: f.name })); return; }
    if (f.size > MAX_FILE_BYTES) { setError(t("applyPage.errTooBig", { name: f.name })); return; }
    setError(null);
    setPortrait(f);
  }

  function removeSample(idx: number) {
    setSamples((prev) => prev.filter((_, i) => i !== idx));
  }

  function setCredit(idx: number, credit: string) {
    setSamples((prev) => prev.map((s, i) => (i === idx ? { ...s, credit } : s)));
  }

  function setTheme(idx: number, theme: string) {
    setSamples((prev) => prev.map((s, i) => (i === idx ? { ...s, theme } : s)));
  }

  async function uploadImages(): Promise<{ urls: string[]; credits: string[]; themes: string[]; portraitUrl: string | null }> {
    const bucket = kind === "visagist" ? "visagist-images" : "photographer-images";
    async function uploadOne(file: File): Promise<string> {
      const optimized = await toWebP(file);
      assertUploadable(optimized);
      const ext = (optimized.name.split(".").pop() ?? "webp").toLowerCase();
      const safe = `${Date.now()}-${crypto.randomUUID()}.${ext}`;
      const path = `applications/${safe}`;
      const { error: upErr } = await supabase.storage
        .from(bucket)
        .upload(path, optimized, { cacheControl: "31536000", contentType: safeContentType(optimized), upsert: false });
      if (upErr) throw new Error(upErr.message);
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
      return pub.publicUrl;
    }
    const urls: string[] = [];
    const credits: string[] = [];
    const sampleThemes: string[] = [];
    const portraitUrl = portrait ? await uploadOne(portrait) : null;
    for (const s of samples) {
      urls.push(await uploadOne(s.file));
      credits.push(s.credit.trim());
      sampleThemes.push(s.theme || defaultTheme);
    }
    return { urls, credits, themes: sampleThemes, portraitUrl };
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submittingRef.current) return;
    setError(null);
    if (!consent) { setError(t("applyPage.errConsent")); return; }
    if (!igConsent) { setError(t("applyPage.errInstagramConsent")); return; }
    if (!portrait) { setError(t("applyPage.errPortrait")); return; }
    if (samples.length === 0) { setError(t("applyPage.errMinOneImage")); return; }
    if (kind === "visagist" && samples.some((s) => s.credit.trim().length < 2)) {
      setError(t("applyPage.errCredit")); return;
    }
    const fd = new FormData(e.currentTarget);
    const themeIds = fd.getAll("themes").map(String);
    const cityValue = (cityChoice === "__other__" ? String(fd.get("city_other") ?? "") : cityChoice).trim();
    const priceRaw = String(fd.get("price_from") ?? "").trim();
    const igRaw = String(fd.get("instagram_handle") ?? "").trim().replace(/^@/, "");
    const schema = z.object({
      full_name: z.string().trim().min(2).max(120),
      email: z.string().trim().email().max(255),
      country: z.enum(countries),
      city: z.string().trim().min(2).max(80),

      website_url: z.string().trim().url().max(500).optional().or(z.literal("")),
      instagram_handle: z.string().trim().regex(/^[A-Za-z0-9_.]{1,30}$/).optional().or(z.literal("")),
      price_from: z.coerce.number().int().min(0).max(100000).optional(),
      bio: z.string().trim().max(2000).optional(),
      themes: z.array(z.string().max(40)).min(1).max(10),
    });
    const parsed = schema.safeParse({
      full_name: fd.get("full_name"),
      email: fd.get("email"),
      country: fd.get("country"),
      city: cityValue,

      website_url: fd.get("website_url") || undefined,
      instagram_handle: igRaw || undefined,
      price_from: priceRaw || undefined,
      bio: fd.get("bio") || undefined,
      themes: themeIds,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("applyPage.errCheckFields"));
      return;
    }
    setBusy(true);
    submittingRef.current = true;
    let uploaded: { urls: string[]; credits: string[]; themes: string[]; portraitUrl: string | null };
    try {
      uploaded = await uploadImages();
    } catch (err) {
      setBusy(false);
      submittingRef.current = false;
      setError(err instanceof Error ? err.message : t("applyPage.errUpload"));
      return;
    }
    const base = {
      full_name: parsed.data.full_name,
      email: parsed.data.email,
      country: parsed.data.country,
      city: parsed.data.city,
      region: parsed.data.city,
      website_url: parsed.data.website_url || null,
      instagram_handle: parsed.data.instagram_handle || null,
      price_from: parsed.data.price_from ?? null,
      tfp: fd.get("tfp") === "on",
      price_on_request: fd.get("price_on_request") === "on",
      bio: parsed.data.bio ?? null,
      themes: parsed.data.themes,
      sample_image_urls: uploaded.urls,
      sample_image_themes: uploaded.themes,
      portrait_url: uploaded.portraitUrl,
      instagram_consent: igConsent,
      instagram_consent_at: igConsent ? new Date().toISOString() : null,
    };
    const { error } = kind === "visagist"
      ? await supabase.from("visagist_applications").insert({ ...base, sample_image_credits: uploaded.credits })
      : await supabase.from("photographer_applications").insert(base);
    setBusy(false);
    if (error) { submittingRef.current = false; setError(error.message); return; }
    // Bestätigungs-E-Mail an Bewerber:in senden (best-effort, blockiert nicht).
    try {
      await fetch("/api/public/application-confirmation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: parsed.data.full_name,
          email: parsed.data.email,
          kind,
        }),
      });
    } catch {
      // Fehler beim E-Mail-Versand soll die erfolgreiche Bewerbung nicht stören.
    }
    // Interne Admin-Benachrichtigung (best-effort, blockiert nicht).
    try {
      await fetch("/api/public/admin-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "application",
          name: parsed.data.full_name,
          kind,
          email: parsed.data.email,
          city: parsed.data.city,
          country: parsed.data.country,
        }),
      });
    } catch {
      // Benachrichtigungsfehler darf die Bewerbung nicht stören.
    }
    setSent(true);
  }

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">{t("applyPage.eyebrow")}</div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">{t("applyPage.titleLine1")}<br />{t("applyPage.titleLine2Pre")}<em className="italic">{t("applyPage.titleEm")}</em>.</h1>
          <p className="mt-8 max-w-xl text-muted-foreground">{t("applyPage.intro")}</p>
          <p className="mt-4 eyebrow">
            <Link
              to={kind === "visagist" ? "/fuer-visagisten" : "/fuer-fotografen"}
              className="text-muted-foreground hover:text-foreground transition-colors border-b border-foreground pb-1"
            >
              Alle Vorteile ansehen →
            </Link>
          </p>
          <div className="mt-8 inline-flex border border-border eyebrow">
            <button type="button" onClick={() => setKind("photographer")} className={`px-5 py-3 ${kind === "photographer" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              {t("applyPage.kindPhotographer")}
            </button>
            <button type="button" onClick={() => setKind("visagist")} className={`px-5 py-3 border-l border-border ${kind === "visagist" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              {t("applyPage.kindVisagist")}
            </button>
          </div>
        </div>
      </section>
      <section>
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow text-muted-foreground">{t("applyPage.howEyebrow")}</div>
            <ol className="mt-6 space-y-6">
              <li><div className="serif text-2xl">{t("applyPage.step1Title")}</div><p className="text-muted-foreground mt-1">{t("applyPage.step1Text")}</p></li>
              <li><div className="serif text-2xl">{t("applyPage.step2Title")}</div><p className="text-muted-foreground mt-1">{t("applyPage.step2Text")}</p></li>
              <li><div className="serif text-2xl">{t("applyPage.step3Title")}</div><p className="text-muted-foreground mt-1">{t("applyPage.step3Text")}</p></li>
            </ol>
          </div>
          <div className="md:col-span-7 border border-border p-8 md:p-12">
            <div className="eyebrow text-muted-foreground">{t("applyPage.formEyebrow")}</div>
            <h2 className="serif text-3xl md:text-4xl mt-3">{t("applyPage.formTitle")}</h2>
            {sent ? (
              <div className="mt-8">
                <p className="text-muted-foreground">{t("applyPage.sentText")}</p>
                <Link to="/" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">{t("applyPage.sentBack")}</Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-8 space-y-6">
                <Field label={t("applyPage.fieldName")}>
                  <input name="full_name" required className="w-full border-b border-foreground bg-transparent py-2 outline-none" />
                </Field>
                <Field label={t("applyPage.fieldEmail")}>
                  <input name="email" type="email" required className="w-full border-b border-foreground bg-transparent py-2 outline-none" />
                </Field>
                <Field label={t("applyPage.fieldCountry")}>
                  <select
                    name="country"
                    required
                    value={country}
                    onChange={(e) => { setCountry(e.target.value as Country); setCityChoice(""); }}
                    className="w-full border-b border-foreground bg-transparent py-2 outline-none"
                  >
                    <option value=""></option>
                    {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label={t("applyPage.fieldCity")}>
                  <select
                    value={cityChoice}
                    onChange={(e) => setCityChoice(e.target.value)}
                    disabled={!country}
                    required
                    className="w-full border-b border-foreground bg-transparent py-2 outline-none disabled:opacity-50"
                  >
                    <option value="">{country ? "" : t("applyPage.cityFirstCountry")}</option>
                    {country && citiesByCountry[country].map((c) => <option key={c} value={c}>{c}</option>)}
                    {country && <option value="__other__">{t("applyPage.cityOther")}</option>}
                  </select>
                  {cityChoice === "__other__" && (
                    <input
                      name="city_other"
                      required
                      placeholder={t("applyPage.cityEnter")}
                      maxLength={80}
                      className="mt-3 w-full border-b border-foreground bg-transparent py-2 outline-none"
                    />
                  )}
                </Field>
                <Field label={t("applyPage.fieldThemes")} hint={t("applyPage.themesHint")}>
                  <div className="flex flex-wrap gap-3 pt-2">
                    {themeOptions.map((t) => (
                      <label key={t.id} className="text-sm flex items-center gap-2">
                        <input type="checkbox" name="themes" value={t.id} /> {t.label}
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label={t("applyPage.fieldPrice")} hint={t("applyPage.priceHint")}>
                  <input name="price_from" type="number" min={0} max={100000} step={50} className="w-full border-b border-foreground bg-transparent py-2 outline-none" />
                </Field>
                <Field label={t("applyPage.fieldPriceOptions")}>
                  <div className="flex flex-wrap gap-6 pt-2">
                    <label className="text-sm flex items-center gap-2">
                      <input type="checkbox" name="tfp" /> {t("applyPage.optionTfp")}
                    </label>
                    <label className="text-sm flex items-center gap-2">
                      <input type="checkbox" name="price_on_request" /> {t("applyPage.optionOnRequest")}
                    </label>
                  </div>
                </Field>
                <Field label={t("applyPage.fieldWebsite")}>
                  <input name="website_url" type="url" placeholder="https://" className="w-full border-b border-foreground bg-transparent py-2 outline-none" />
                </Field>
                <Field label={t("applyPage.fieldInstagram")} hint={t("applyPage.instagramHint")}>
                  <input name="instagram_handle" type="text" placeholder={t("applyPage.instagramPlaceholder")} maxLength={30} className="w-full border-b border-foreground bg-transparent py-2 outline-none" />
                </Field>
                <Field label={t("applyPage.fieldBio")} hint={t("applyPage.bioHint")}>
                  <textarea name="bio" rows={4} className="w-full border-b border-foreground bg-transparent py-2 outline-none resize-none" />
                </Field>
                <Field label={t("applyPage.fieldPortrait")} hint={t("applyPage.portraitHint")}>
                  <div className="pt-2 space-y-3">
                    {!portrait ? (
                      <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-foreground/40 bg-muted/20 hover:bg-muted/40 hover:border-foreground transition-colors cursor-pointer px-4 py-6 text-center">
                        <span className="serif text-sm">{t("applyPage.choosePortrait")}</span>
                        <input
                          type="file"
                          accept={STATIC_IMAGE_ACCEPT}
                          onChange={(e) => { setPortraitFile(e.target.files); e.target.value = ""; }}
                          className="sr-only"
                        />
                      </label>
                    ) : (
                      <div className="flex gap-3 border border-border p-3 items-center">
                        <img src={URL.createObjectURL(portrait)} alt="" className="w-20 h-20 object-cover shrink-0" />
                        <div className="flex-1 min-w-0 text-xs text-muted-foreground truncate">{portrait.name}</div>
                        <button
                          type="button"
                          onClick={() => setPortrait(null)}
                          className="eyebrow text-xs text-muted-foreground hover:text-foreground self-start"
                        >{t("applyPage.remove")}</button>
                      </div>
                    )}
                  </div>
                </Field>
                <Field
                  label={t("applyPage.fieldImages", { max: MAX_SAMPLES })}
                  hint={kind === "visagist"
                    ? t("applyPage.imagesHintVisagist")
                    : t("applyPage.imagesHintPhotographer")}
                >
                  <div className="pt-2 space-y-3">
                    {samples.length < MAX_SAMPLES && (
                      <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-foreground/40 bg-muted/20 hover:bg-muted/40 hover:border-foreground transition-colors cursor-pointer px-4 py-6 text-center">
                        <span className="serif text-sm">{t("applyPage.chooseImages")}</span>
                        <span className="text-xs text-muted-foreground">{t("applyPage.imagesAdded", { count: samples.length, max: MAX_SAMPLES })}</span>
                        <input
                          type="file"
                          accept={STATIC_IMAGE_ACCEPT}
                          multiple
                          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                          className="sr-only"
                        />
                      </label>
                    )}
                    {samples.length > 0 && (
                      <ul className="space-y-3">
                        {samples.map((s, idx) => (
                          <li key={idx} className="flex gap-3 border border-border p-3">
                            <img
                              src={URL.createObjectURL(s.file)}
                              alt=""
                              className="w-20 h-20 object-cover shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground truncate">{s.file.name}</div>
                              <select
                                value={s.theme}
                                onChange={(e) => setTheme(idx, e.target.value)}
                                className="mt-2 w-full border-b border-foreground bg-transparent py-1 text-sm outline-none"
                              >
                                {themeOptions.map((opt) => (
                                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                                ))}
                              </select>
                              {kind === "visagist" ? (
                                <input
                                  type="text"
                                  value={s.credit}
                                  onChange={(e) => setCredit(idx, e.target.value)}
                                  required
                                  maxLength={120}
                                  placeholder={t("applyPage.creditPlaceholder")}
                                  className="mt-2 w-full border-b border-foreground bg-transparent py-1 text-sm outline-none"
                                />
                              ) : null}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSample(idx)}
                              className="eyebrow text-xs text-muted-foreground hover:text-foreground self-start"
                            >{t("applyPage.remove")}</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Field>
                <label className="flex gap-3 text-xs text-muted-foreground border border-border p-3">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    className="mt-0.5 shrink-0"
                  />
                  <span>
                    {t("applyPage.consentBefore", { kind: kind === "visagist" ? t("applyPage.kindWorks") : t("applyPage.kindImages") })}
                    <Link to="/datenschutz" className="underline">{t("applyPage.consentLink")}</Link>{t("applyPage.consentAfter")}
                  </span>
                </label>
                <label className="flex gap-3 text-xs text-muted-foreground border border-border p-3">
                  <input
                    type="checkbox"
                    checked={igConsent}
                    onChange={(e) => setIgConsent(e.target.checked)}
                    required
                    className="mt-0.5 shrink-0"
                  />
                  <span>
                    {t("applyPage.instagramConsent")}
                  </span>
                </label>
                {error && <div className="text-sm text-destructive">{error}</div>}
                <button type="submit" disabled={busy} className="eyebrow border border-foreground px-6 py-3 hover:bg-foreground hover:text-background transition-colors disabled:opacity-50">
                  {busy ? t("applyPage.submitBusy") : t("applyPage.submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="eyebrow text-muted-foreground mb-2">{label}</div>
      {children}
      {hint && <div className="mt-2 text-xs italic text-muted-foreground">{hint}</div>}
    </label>
  );
}